import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  BarChart3, Users, Package, RefreshCw, DollarSign, TrendingUp, AlertTriangle,
  Download, ArrowRight, Clock, CheckCircle, Bell, Percent
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BarChart = ({ data, barKey, label, color = '#10b981' }) => {
  if (!data.length) return null;
  const max = Math.max(...data.map(d => d[barKey] || 0), 1);
  return (
    <div>
      <p className="text-xs text-slate-500 mb-2">{label}</p>
      <div className="flex items-end gap-[2px] h-24">
        {data.map((d, i) => (
          <div key={d.date ?? `bar-${i}`} className="flex-1 flex flex-col items-center gap-0.5">
            <div className="w-full rounded-t-sm transition-all duration-300 min-h-[2px]"
              style={{ height: `${Math.max(2, (d[barKey] / max) * 100)}%`, backgroundColor: color }}
              title={`${d.date}: $${(d[barKey] || 0).toFixed(2)}`} />
            {i % Math.ceil(data.length / 8) === 0 && <span className="text-[8px] text-slate-400">{d.date}</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminAnalyticsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const h = { headers: getAuthHeaders() };
      const [a, al] = await Promise.all([
        axios.get(`${API}/admin/analytics?period=${period}`, h),
        axios.get(`${API}/admin/alerts`, h),
      ]);
      setAnalytics(a.data);
      setAlerts(al.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [period, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const exportReport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/export-report?period=${period}&format=csv`, {
        headers: getAuthHeaders(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_${period}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Reporte descargado');
    } catch (e) { toast.error('Error al exportar'); }
    finally { setExporting(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  const a = analytics;

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard Ejecutivo</h1>
          <p className="text-slate-500">Control total de operaciones</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportReport} disabled={exporting} variant="outline" size="sm" data-testid="export-report-btn">
            <Download className="w-4 h-4 mr-1" /> {exporting ? 'Exportando...' : 'Exportar CSV'}
          </Button>
        </div>
      </div>

      {/* Period Filter */}
      <div className="flex gap-2">
        {[{ v: 'week', l: 'Semanal' }, { v: 'month', l: 'Mensual' }, { v: 'year', l: 'Anual' }].map((p) => (
          <Button key={p.v} variant={period === p.v ? 'default' : 'outline'} size="sm" onClick={() => setPeriod(p.v)}
            className={period === p.v ? 'bg-blue-700' : ''} data-testid={`period-${p.v}`}>
            {p.l}
          </Button>
        ))}
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((al, i) => (
            <div key={al.id ?? `${al.type}-${al.title}-${i}`} className={`flex items-center gap-3 p-3 rounded-xl border ${al.type === 'alert' ? 'bg-red-50 border-red-200' : al.type === 'warning' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`} data-testid={`alert-${i}`}>
              {al.type === 'alert' ? <AlertTriangle className="w-5 h-5 text-red-500" /> : al.type === 'warning' ? <Bell className="w-5 h-5 text-amber-500" /> : <Clock className="w-5 h-5 text-blue-500" />}
              <div className="flex-1">
                <p className="text-sm font-medium">{al.title}</p>
                <p className="text-xs text-slate-600">{al.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Today Summary */}
      <Card className="p-5 bg-gradient-to-r from-blue-600 to-blue-800 text-white border-0">
        <h3 className="text-blue-200 text-sm mb-3">Resumen de hoy</h3>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <p className="text-2xl font-bold">{a?.today?.orders || 0}</p>
            <p className="text-xs text-blue-200">Ordenes</p>
          </div>
          <div>
            <p className="text-2xl font-bold">${(a?.today?.divisa_volume || 0).toFixed(0)}</p>
            <p className="text-xs text-blue-200">Vol. Divisas</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-300">${(a?.today?.commissions || 0).toFixed(2)}</p>
            <p className="text-xs text-blue-200">Comisiones</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-yellow-300">${(a?.today?.profits || 0).toFixed(2)}</p>
            <p className="text-xs text-blue-200">Ganancias</p>
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4 border-0 shadow-sm" data-testid="kpi-clients">
          <Users className="w-8 h-8 text-violet-500 mb-2" />
          <p className="text-2xl font-bold">{a?.total_clients || 0}</p>
          <p className="text-sm text-slate-500">Clientes totales</p>
          <p className="text-xs text-green-500 mt-1">+{a?.new_clients || 0} nuevos</p>
        </Card>
        <Card className="p-4 border-0 shadow-sm" data-testid="kpi-orders">
          <Package className="w-8 h-8 text-blue-500 mb-2" />
          <p className="text-2xl font-bold">{a?.orders?.total || 0}</p>
          <p className="text-sm text-slate-500">Ordenes</p>
          <p className="text-xs text-amber-500 mt-1">{a?.orders?.pending || 0} pendientes</p>
        </Card>
        <Card className="p-4 border-0 shadow-sm" data-testid="kpi-divisas">
          <RefreshCw className="w-8 h-8 text-emerald-500 mb-2" />
          <p className="text-2xl font-bold">{a?.divisas?.total || 0}</p>
          <p className="text-sm text-slate-500">Op. Divisas</p>
          <p className="text-xs text-slate-400 mt-1">Vol: ${(a?.divisas?.volume || 0).toFixed(0)}</p>
        </Card>
        <Card className="p-4 border-0 shadow-sm" data-testid="kpi-profits">
          <TrendingUp className="w-8 h-8 text-green-500 mb-2" />
          <p className="text-2xl font-bold text-green-600">${((a?.divisas?.commissions || 0) + (a?.divisas?.profits || 0)).toFixed(2)}</p>
          <p className="text-sm text-slate-500">Ingresos totales</p>
          <p className="text-xs text-slate-400 mt-1">Com: {a?.commission_pct}% | Gan: {a?.profit_pct}%</p>
        </Card>
      </div>

      {/* Commission & Profit Gauges */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="p-5 border-0 shadow-sm" data-testid="commission-gauge">
          <div className="flex items-center gap-3 mb-3">
            <Percent className="w-6 h-6 text-blue-500" />
            <h3 className="font-semibold">Comisiones del periodo</h3>
          </div>
          <p className="text-3xl font-bold text-blue-600">${(a?.divisas?.commissions || 0).toFixed(2)}</p>
          <div className="w-full bg-slate-100 rounded-full h-3 mt-3">
            <div className="bg-blue-500 h-3 rounded-full transition-all" style={{ width: `${Math.min(100, a?.commission_pct || 0) * 10}%` }} />
          </div>
          <p className="text-xs text-slate-500 mt-1">{a?.commission_pct || 0}% del volumen</p>
        </Card>
        <Card className="p-5 border-0 shadow-sm" data-testid="profit-gauge">
          <div className="flex items-center gap-3 mb-3">
            <DollarSign className="w-6 h-6 text-green-500" />
            <h3 className="font-semibold">Ganancias del periodo</h3>
          </div>
          <p className="text-3xl font-bold text-green-600">${(a?.divisas?.profits || 0).toFixed(2)}</p>
          <div className="w-full bg-slate-100 rounded-full h-3 mt-3">
            <div className="bg-green-500 h-3 rounded-full transition-all" style={{ width: `${Math.min(100, a?.profit_pct || 0) * 10}%` }} />
          </div>
          <p className="text-xs text-slate-500 mt-1">{a?.profit_pct || 0}% del volumen</p>
        </Card>
      </div>

      {/* Charts */}
      {a?.daily_data && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-5 border-0 shadow-sm">
            <BarChart data={a.daily_data} barKey="orders_volume" label="Volumen de ordenes (USD)" color="#3b82f6" />
          </Card>
          <Card className="p-5 border-0 shadow-sm">
            <BarChart data={a.daily_data} barKey="divisa_volume" label="Volumen de divisas (USD)" color="#8b5cf6" />
          </Card>
          <Card className="p-5 border-0 shadow-sm">
            <BarChart data={a.daily_data} barKey="commissions" label="Comisiones diarias (USD)" color="#06b6d4" />
          </Card>
          <Card className="p-5 border-0 shadow-sm">
            <BarChart data={a.daily_data} barKey="profits" label="Ganancias diarias (USD)" color="#10b981" />
          </Card>
        </div>
      )}

      {/* Top Services */}
      {a?.top_services?.length > 0 && (
        <Card className="p-5 border-0 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-3">Servicios mas populares</h3>
          <div className="space-y-2">
            {a.top_services.map((s) => (
              <div key={s.name} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                <span className="text-sm font-medium">{s.name}</span>
                <span className="text-sm font-bold text-blue-600">{s.count} ordenes</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default AdminAnalyticsPage;
